import { RELATION_PART_OF } from '@backstage/catalog-model';
import { TableColumn } from '@backstage/core-components';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import {
  EntityRefLinks,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { SystemCell } from '../service-group-columns/SystemCell';

function getServiceGroupRelations(row: CatalogTableRow) {
  return getEntityRelations(row.entity, RELATION_PART_OF, {
    kind: 'servicegroup',
  });
}

function createServiceGroupColumn(): TableColumn<CatalogTableRow> {
  return {
    title: 'Service Group',
    customFilterAndSearch: (query, row) => {
      const names = getServiceGroupRelations(row)
        .map(ref => ref.name)
        .join(', ')
        .toLocaleUpperCase('en-US');
      return names.includes(query.toLocaleUpperCase('en-US'));
    },
    render: row => (
      <EntityRefLinks
        entityRefs={getServiceGroupRelations(row)}
        defaultKind="servicegroup"
      />
    ),
  };
}

// CatalogTableRow ({entity, resolved}) isn't itself an Entity, so the shared
// generic factories in modules/serviceGroupColumns don't apply directly here
// - this just adapts the shared SystemCell to read row.entity.
function createServiceGroupAwareSystemColumn(): TableColumn<CatalogTableRow> {
  return {
    title: 'System',
    render: row => <SystemCell entity={row.entity} />,
  };
}

/**
 * Wraps the stock Catalog table columns for Components: swaps in a
 * ServiceGroup-aware System column and adds a Service Group column right
 * next to it. Reuses CatalogTable.defaultColumnsFunc rather than
 * reimplementing kind-by-kind column selection, so anything upstream changes
 * about non-Component kinds keeps working unmodified.
 */
export const catalogTableColumnsFunc: CatalogTableColumnsFunc =
  entityListContext => {
    const baseColumns = CatalogTable.defaultColumnsFunc(entityListContext);
    const effectiveKind =
      entityListContext.entities[0]?.kind?.toLocaleLowerCase('en-US') ??
      entityListContext.filters.kind?.value;

    if (effectiveKind !== 'component') {
      return baseColumns;
    }

    // baseColumns for the component case is always
    // [Title(hidden), Name, System, Owner, Type, Lifecycle, ...] - index 2 is
    // always System here, replaced in place so column order doesn't shift.
    return [
      ...baseColumns.slice(0, 2),
      createServiceGroupAwareSystemColumn(),
      createServiceGroupColumn(),
      ...baseColumns.slice(3),
    ];
  };
